import { useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { FONT_MONO } from '../theme';
import { Button, Card } from '../ui';
import { CardTitle, extraColors } from './common';
import {
  EXCLUDED_REASONS, GOAL_LABELS, calorieTargetPatch, fmtInt, signed,
} from './nutritionData';
import { dayLabel } from './statsData';

// A smaller move than this is inside the estimate's noise.
const MIN_CHANGE = 100;

function goalRateText(est) {
  const p = est.goalRatePct;
  if (!p) return 'hold weight';
  const kg = est.goalRateKgPerWeek !== null ? ` (${signed(est.goalRateKgPerWeek)} kg/wk)` : '';
  return `${p > 0 ? '+' : '−'}${Math.abs(p)}% BW/wk${kg}`;
}

/* Maintenance (TDEE) from logged intake and the weight trend, via
 * logic/energy estimateEnergyBalance: the number Claude gets from
 * get_energy_balance. It can turn the goal's suggestion into the calorie
 * target after a confirm; protein stays put and carbs take the difference. */
export function EnergyBalanceCard({ t, isDark, est, profile, setState, onOpenDay }) {
  const [showExcluded, setShowExcluded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [applied, setApplied] = useState(null);
  const c = extraColors(isDark);
  if (!est) return null;

  const current = Number(profile?.calorieTarget) || 0;
  const delta = est.suggestedKcal !== null ? est.suggestedKcal - current : null;
  const patch = est.suggestedKcal !== null ? calorieTargetPatch(profile, est.suggestedKcal) : null;
  const carbsNow = Number(profile?.macros?.carbs) || 0;
  const goalLabel = GOAL_LABELS[est.goal] || 'No goal set';
  const excluded = [...est.excluded].reverse();

  const apply = () => {
    if (!patch || !setState) return;
    setState(prev => {
      const next = calorieTargetPatch(prev.profile, est.suggestedKcal);
      return { ...prev, profile: { ...prev.profile, ...next, macros: { ...(prev.profile?.macros || {}), ...next.macros } } };
    });
    setConfirming(false);
    setApplied(est.suggestedKcal);
  };

  return (
    <Card t={t} padding={16}>
      <CardTitle t={t} sub={`Maintenance from your logged intake and weight trend, ${est.days} days to yesterday.`}>
        Energy balance
      </CardTitle>

      {est.ok ? (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textFaint }}>
            Maintenance
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
            <span style={{ fontSize: 28, fontFamily: FONT_MONO, fontWeight: 700, color: t.text }}>≈ {fmtInt(est.tdee)}</span>
            <span style={{ fontSize: 14, fontFamily: FONT_MONO, fontWeight: 600, color: t.textMuted }}>±{fmtInt(est.plusMinus)} kcal</span>
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 6, lineHeight: 1.5 }}>
            {[
              `${est.days} d`, `${est.completeDays} complete`, `${est.weighIns} weigh-ins`,
              `trend ${signed(est.slopeKgPerWeek)} kg/wk (${est.days}-day)`,
            ].map((part, i) => (
              <span key={part}>{i ? ' · ' : ''}<span style={{ whiteSpace: 'nowrap' }}>{part}</span></span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5 }}>
            Avg intake {fmtInt(est.avgIntake)} kcal on complete days
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 700, color: t.text }}>Not enough data yet</div>
          <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, lineHeight: 1.45 }}>
            {est.need ? `${est.need.charAt(0).toUpperCase()}${est.need.slice(1)}.` : 'The weight trend is still too flat to fit.'}
          </div>
          <div style={{ fontSize: 12, color: t.textFaint, marginTop: 4, fontFamily: FONT_MONO }}>
            {est.completeDays}/{est.neededDays} complete days · {est.weighIns}/8 weigh-ins
          </div>
        </>
      )}

      {est.caveats.map(cv => (
        <div key={cv.code} style={{ fontSize: 12, color: c.amber, background: c.amberSoft, borderRadius: 8, padding: '8px 10px', marginTop: 10, lineHeight: 1.45 }}>
          {cv.text}
        </div>
      ))}

      {excluded.length > 0 && (
        <>
          <button type="button" aria-expanded={showExcluded} onClick={() => setShowExcluded(v => !v)} style={{
            marginTop: 10, minHeight: 40, width: '100%', padding: '0 2px', background: 'transparent', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
            color: t.textMuted, fontSize: 12, fontWeight: 600, borderTop: `1px solid ${t.divider}`,
          }}>
            <span>{excluded.length} day{excluded.length === 1 ? '' : 's'} left out</span>
            {showExcluded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
          {showExcluded && (
            <div>
              {excluded.map(x => (
                <button key={x.date} type="button" onClick={() => onOpenDay && onOpenDay(x.date)} style={{
                  width: '100%', minHeight: 40, padding: '0 2px', background: 'transparent', border: 'none',
                  borderTop: `1px solid ${t.divider}`, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: t.text, width: 88, flexShrink: 0 }}>{dayLabel(x.date)}</span>
                  <span style={{ fontSize: 12, color: t.textMuted, flex: 1, minWidth: 0 }}>{EXCLUDED_REASONS[x.reason] || x.reason}</span>
                  <ChevronRight size={14} color={t.textFaint} />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {est.ok && (
        <div style={{ marginTop: 10, paddingTop: 12, borderTop: `1px solid ${t.divider}` }}>
          <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.45 }}>
            Goal: <b style={{ color: t.text }}>{goalLabel}</b> · {goalRateText(est)}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: t.text }}>Suggested</span>
            <span style={{ fontSize: 18, fontFamily: FONT_MONO, fontWeight: 700, color: t.accent }}>{fmtInt(est.suggestedKcal)} kcal</span>
            {current > 0 && (
              <span style={{ fontSize: 12, color: t.textMuted }}>{signed(delta, 0)} vs your {fmtInt(current)}</span>
            )}
          </div>

          {applied !== null && applied === current ? (
            <div style={{ fontSize: 12, color: t.accent, marginTop: 10 }}>
              Calorie target set to {fmtInt(applied)}. Give it two weeks before the next change.
            </div>
          ) : current > 0 && Math.abs(delta) < MIN_CHANGE ? (
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 8, lineHeight: 1.45 }}>
              Your target is within {MIN_CHANGE} kcal of this, so no change is needed.
            </div>
          ) : confirming ? (
            <div style={{ marginTop: 10, background: t.surfaceAlt, borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 13, color: t.text, lineHeight: 1.45 }}>
                Set calories {current > 0 ? `${fmtInt(current)} → ` : ''}<b>{fmtInt(est.suggestedKcal)}</b>?
                {current > 0 && ` Protein stays ${fmtInt(profile?.macros?.protein)} g, carbs ${fmtInt(carbsNow)} → ${fmtInt(patch.macros.carbs)} g, fats stay ${fmtInt(profile?.macros?.fats)} g.`}
              </div>
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 6, lineHeight: 1.45 }}>
                Change the target at most once every 14 days: the estimate needs two weeks of new data to show the effect.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                <Button t={t} variant="ghost" onClick={() => setConfirming(false)} style={{ minHeight: 40 }}>Cancel</Button>
                <Button t={t} variant="primary" onClick={apply} style={{ minHeight: 40 }}>Set target</Button>
              </div>
            </div>
          ) : setState ? (
            <Button t={t} variant="default" onClick={() => setConfirming(true)}
              style={{ marginTop: 10, minHeight: 40, width: '100%', justifyContent: 'center' }}>
              Use as calorie target
            </Button>
          ) : null}
        </div>
      )}
    </Card>
  );
}
